import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Terminal, Loader2, Shield, Zap } from 'lucide-react';

const Login = () => {
  const navigate = useNavigate();
  const { login, showBoot } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [typedTitle, setTypedTitle] = useState('');
  const fullTitle = 'AUTO_EDITO';

  // Typing effect for title
  useEffect(() => {
    let index = 0;
    const timer = setInterval(() => {
      if (index <= fullTitle.length) {
        setTypedTitle(fullTitle.slice(0, index));
        index++;
      } else {
        clearInterval(timer);
      }
    }, 100);
    return () => clearInterval(timer);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const success = await login(email, password);
    
    if (success) {
      navigate('/');
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-black relative overflow-hidden" data-testid="login-page">
      {/* Background grid */}
      <div className="absolute inset-0 opacity-10"
           style={{
             backgroundImage: `
               linear-gradient(rgba(0,229,255,0.1) 1px, transparent 1px),
               linear-gradient(90deg, rgba(0,229,255,0.1) 1px, transparent 1px)
             `,
             backgroundSize: '50px 50px'
           }}
      />

      {/* Scanlines */}
      <div className="fixed inset-0 pointer-events-none opacity-[0.03] z-50"
           style={{
             backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,255,255,0.1) 2px, rgba(0,255,255,0.1) 4px)'
           }}
      />

      {/* Glow effects */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-sky-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />

      <div className="w-full max-w-md relative z-10">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <div className="relative">
              <Terminal className="w-20 h-20 text-sky-400" style={{ filter: 'drop-shadow(0 0 20px rgba(56,189,248,0.5))' }} />
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full animate-pulse" />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold font-mono tracking-widest uppercase text-sky-400 mb-2"
              style={{ textShadow: '0 0 30px rgba(0,229,255,0.5), 0 0 60px rgba(0,229,255,0.3)' }}>
            {typedTitle}<span className="animate-pulse">_</span>
          </h1>
          <p className="text-slate-500 font-mono text-sm tracking-wider">
            [ COMMAND CENTER v3.7.2 ]
          </p>
          <p className="text-slate-600 font-mono text-xs mt-1">
            MILITARY GRADE PUBLISHING SYSTEM
          </p>
        </div>

        {/* Login Card */}
        <Card className="relative bg-slate-950/80 backdrop-blur-xl border border-sky-900/50 overflow-hidden">
          {/* Corner markers */}
          <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-sky-500" />
          <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-sky-500" />
          <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-sky-500" />
          <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-sky-500" />

          <CardHeader className="border-b border-slate-800/50">
            <CardTitle className="text-xl font-mono uppercase text-sky-400 text-center flex items-center justify-center gap-2">
              <Shield className="w-5 h-5" />
              ACCESS_SYSTEM
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="text-xs text-slate-500 uppercase tracking-wider font-mono flex items-center gap-2">
                  <Zap className="w-3 h-3 text-sky-500" />
                  EMAIL_ADDRESS
                </label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="operateur@command.mil"
                  required
                  data-testid="input-email"
                  className="mt-2 bg-slate-900/50 border-slate-700 text-sky-300 placeholder:text-slate-600 focus:border-sky-500/50 focus:ring-1 focus:ring-sky-500/50 font-mono"
                />
              </div>

              <div>
                <label className="text-xs text-slate-500 uppercase tracking-wider font-mono flex items-center gap-2">
                  <Zap className="w-3 h-3 text-sky-500" />
                  ACCESS_CODE
                </label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••••••"
                  required
                  data-testid="input-password"
                  className="mt-2 bg-slate-900/50 border-slate-700 text-sky-300 placeholder:text-slate-600 focus:border-sky-500/50 focus:ring-1 focus:ring-sky-500/50 font-mono"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                data-testid="btn-login"
                className="w-full bg-sky-500/10 hover:bg-sky-500/20 text-sky-400 border-2 border-sky-500/50 hover:border-sky-400 uppercase tracking-widest font-mono font-bold py-4 transition-all duration-300 rounded-none text-sm"
                style={{
                  clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%)'
                }}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    INITIALIZING_SYSTEM...
                  </>
                ) : (
                  '[ AUTHENTICATE ]'
                )}
              </Button>
            </form>

            {/* Security notice */}
            <div className="mt-6 pt-4 border-t border-slate-800/50">
              <p className="text-[10px] text-slate-600 font-mono text-center">
                ⚠ AUTHORIZED PERSONNEL ONLY ⚠
                <br />
                All access attempts are logged and monitored
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-slate-600 text-xs mt-8 font-mono">
          © 2026 <span className="text-sky-500">LPP_LOGICIEL</span> & <span className="text-sky-500">JB_GIRAUD</span>
          <br />
          <span className="text-slate-700">Flux capaciteur: 1.21 GW | Réponse: 42</span>
        </p>
      </div>
    </div>
  );
};

export default Login;
